import React, { useCallback, useEffect, useState } from 'react';
import { useResourceHubService } from '../../services/ResourceHubService';
import useLoadingEffect from '@dr-pam/common-components/Hooks/useLoadingEffect';
import FilterSelector from './FilterSelector';
import Loading from '@dr-pam/common-components/Components/Misc/Loading';
import useLoadTracker from '@dr-pam/common-components/Hooks/useLoadTracker';
import NotificationUtils from '@dr-pam/common-components/Utils/NotificationUtils';
import ResourceList from './ResourceList';
import type {
	Category,
	ResourceHubFilterOptions,
	ResourceHubResource,
	ResourceType,
} from '@dr-pam/common-components/Models/NdcResourceHub';
import SearchInput from '@dr-pam/common-components/Components/Form/SearchInput';
import BackButton from '@dr-pam/common-components/Components/Form/BackButton';

export type ResourceHubProps = {
	className?: string;
	hubIdentifier: string;
};

export default function ResourceHub(props: ResourceHubProps) {
	const { className, hubIdentifier } = props;

	const { isLoading, addLoader, removeLoader } = useLoadTracker();
	const ndcService = useResourceHubService();

	const [categories, setCategories] = useState<Category[]>([]);
	const [resourceTypes, setResourceTypes] = useState<ResourceType[]>([]);
	const [resources, setResources] = useState<ResourceHubResource[]>([]);
	const [filterOptions, setFilterOptions] = useState<ResourceHubFilterOptions | undefined>(undefined);
	const [primaryFilter, setPrimaryFilter] = useState<Category | ResourceType | undefined>(undefined);
	const [selectedCategory, setSelectedCategory] = useState<Category | undefined>(undefined);
	const [selectedResourceType, setSelectedResourceType] = useState<ResourceType | undefined>(undefined);
	const [searchQuery, setSearchQuery] = useState<string>('');

	const isLoadingCategories = useLoadingEffect(async () => {
		try {
			const request = await ndcService.getCategories(hubIdentifier);
			const categories = await request.response;
			setCategories(categories.filter((x) => !x.parentId));
		} catch (err) {
			NotificationUtils.showError(err as Error, 'Failed to fetch categories');
		}
	}, [ndcService, hubIdentifier]);

	const isLoadingResourceTypes = useLoadingEffect(async () => {
		try {
			const request = await ndcService.getResourceTypes(hubIdentifier);
			const resourceTypes = await request.response;
			setResourceTypes(resourceTypes);
		} catch (err) {
			NotificationUtils.showError(err as Error, 'Failed to fetch resource types');
		}
	}, [ndcService]);

	const useFilters =
		filterOptions && Object.values(filterOptions).filter((x) => x !== undefined && x !== null).length > 0;

	const fetchResources = useCallback(async () => {
		const loader = addLoader();
		try {
			const request = useFilters
				? ndcService.filterResources(hubIdentifier, filterOptions)
				: ndcService.getRecentResources(hubIdentifier);
			const resources = await request.response;
			setResources(resources);
		} catch (err) {
			NotificationUtils.showError(err as Error, 'Failed to fetch resources');
		} finally {
			removeLoader(loader);
		}
	}, [addLoader, filterOptions, removeLoader, ndcService, useFilters, hubIdentifier]);

	useEffect(() => {
		fetchResources();
	}, [fetchResources]);

	useEffect(() => {
		setSelectedCategory(undefined);
		setSelectedResourceType(undefined);
		setPrimaryFilter(undefined);
		setFilterOptions(undefined);
		setSearchQuery('');
	}, [hubIdentifier]);

	const handleCategoryChanged = (category: Category | undefined) => {
		setSelectedCategory(category);
		setFilterOptions((current) => ({
			...current,
			categoryId: category?.id,
		}));

		if (!primaryFilter) {
			setPrimaryFilter(category);
		}
	};

	const handleResourceTypeChanged = (resourceType: ResourceType | undefined) => {
		setSelectedResourceType(resourceType);
		setFilterOptions((current) => ({
			...current,
			resourceTypeId: resourceType?.id,
		}));

		if (!primaryFilter) {
			setPrimaryFilter(resourceType);
		}
	};

	const handleSearch = async (query: string) => {
		setFilterOptions((current) => ({
			...current,
			query: query || undefined,
		}));
	};

	const handleBackClicked = () => {
		setSelectedCategory(undefined);
		setSelectedResourceType(undefined);
		setPrimaryFilter(undefined);
		setFilterOptions(undefined);
		setSearchQuery('');
	};

	const isAnyLoading = isLoading || isLoadingCategories || isLoadingResourceTypes;

	return (
		<div className={`ResourceHub ${className ?? ''}`}>
			{primaryFilter && (
				<BackButton className="subtle" onClick={handleBackClicked}>
					Back to resource hub
				</BackButton>
			)}
			<div className="header">
				<h1>Resource hub</h1>
				<SearchInput
					placeholder="Search resource hub"
					value={searchQuery}
					onChange={setSearchQuery}
					onSearch={handleSearch}
				/>
			</div>
			{!categories.find((x) => x.id === primaryFilter?.id) && categories.length ? (
				<FilterSelector
					className="articles"
					title="Categories"
					items={categories}
					keyProp="id"
					nameProp="name"
					value={selectedCategory}
					onChange={handleCategoryChanged}
				/>
			) : null}
			{!resourceTypes.find((x) => x.id === primaryFilter?.id) && resourceTypes.length ? (
				<FilterSelector
					className="resource-types"
					title="Resource Types"
					items={resourceTypes}
					keyProp="id"
					nameProp="name"
					value={selectedResourceType}
					onChange={handleResourceTypeChanged}
				/>
			) : null}
			{isAnyLoading ? <Loading size="large" /> : null}
			{!isAnyLoading &&
				(resources.length ? (
					<>
						{useFilters ? <h2>Search results</h2> : <h2>Recently viewed</h2>}
						<ResourceList
							resourceTypes={resourceTypes}
							categories={categories}
							resources={resources}
							className="list-container"
						/>
					</>
				) : useFilters ? (
					<p className="no-results search">No resources found matching your search criteria.</p>
				) : (
					<p className="no-results recent">Your recently viewed resources will appear here.</p>
				))}
		</div>
	);
}
